import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/useAppStore';
import { getFlatToppings } from '../../utils/orderUtils';
import { formatDuration } from '../../utils/bakingTime';
import { useBakingTimer } from '../../hooks/useBakingTimer';
import { useCustomerOrders } from '../../hooks/useCustomerOrders';
import StickyBanner from '../ui/StickyBanner';
import ProgressBar from '../ui/ProgressBar';

function ReadyBanner({ order }) {
  const { t } = useTranslation();
  return (
    <StickyBanner accentClass="border-l-emerald-500" floatingClass="mt-4">
      <p className="text-sm font-semibold theme-accent">
        {order?.orderNo ? `#${order.orderNo} ` : ''}{t('banner.ready')}
      </p>
    </StickyBanner>
  );
}

function BakingBanner({ customerOrder, otherOrders }) {
  const { t } = useTranslation();
  const config = useAppStore((state) => state.config);
  const timer = useBakingTimer(customerOrder);

  if (timer.remaining <= 0) {
    return <ReadyBanner order={customerOrder} />;
  }

  const otherToppings = otherOrders.flatMap((order) => getFlatToppings(order, config));

  return (
    <StickyBanner accentClass="border-l-emerald-500" floatingClass="mt-4">
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold theme-accent">
          {customerOrder.orderNo ? `#${customerOrder.orderNo} ` : ''}{t('banner.bakingCustomer')}
        </span>
        <span className="text-sm font-semibold theme-accent">
          {t('banner.remaining', { time: formatDuration(timer.remaining) })}
        </span>
      </div>
      <div className="mt-2">
        <ProgressBar progress={timer.progress} fillClass="bg-gradient-to-r from-emerald-400 to-emerald-600" />
      </div>
      {otherOrders.length > 0 && (
        <p className="mt-2 text-xs theme-muted">
          <span className="font-semibold theme-accent">{t('ts.toppings')}:</span>{' '}
          {otherToppings.join(', ')}
        </p>
      )}
    </StickyBanner>
  );
}

function CustomerBakingBanner() {
  const customerOrders = useCustomerOrders();

  const active = customerOrders.filter((order) => order.state !== 'served');
  if (active.length === 0) return null;

  const bakingOrder = active.find((order) => order.state === 'baking' && order.bakingStartedAt);
  if (bakingOrder) {
    const otherActive = active.filter((order) => order.state !== 'baking' && order.state !== 'done');
    return <BakingBanner customerOrder={bakingOrder} otherOrders={otherActive} />;
  }

  if (active.some((order) => order.state === 'done')) {
    const doneOrder = active.find((order) => order.state === 'done');
    return <ReadyBanner order={doneOrder} />;
  }

  return null;
}

export default CustomerBakingBanner;
